"use client";

import React, { useState, useRef } from "react";
import { motion, useInView, AnimatePresence } from "framer-motion";
import { Element } from "react-scroll";
import PortfolioCard from "./ui/portfolio-card";
import FeaturedCard from "./portfolio/featured-card";
import TabButton from "./portfolio/tab-button";
import {
  cardEntry,
  fadeUp,
  gridStagger,
} from "@/constants/portfolio-constants";

const Portfolio = ({ projects, frontend, mern, full }) => {
  const [tab, setTab] = useState("all");
  const [counts, setCounts] = useState({
    all: 8,
    frontend: 4,
    "mern-stack": 4,
    "full-stack": 4,
  });

  const headRef = useRef(null);
  const gridRef = useRef(null);
  const headInView = useInView(headRef, { once: true, margin: "-60px" });
  const gridInView = useInView(gridRef, { once: true, margin: "-80px" });

  const dataMap = {
    all: projects,
    frontend: frontend,
    "mern-stack": mern,
    "full-stack": full,
  };

  const tabs = [
    { id: "all", label: "All", count: projects?.length || 0 },
    { id: "frontend", label: "Frontend", count: frontend?.length || 0 },
    { id: "mern-stack", label: "MERN", count: mern?.length || 0 },
    { id: "full-stack", label: "Full Stack", count: full?.length || 0 },
  ];

  const allData = dataMap[tab] || [];
  const currentCount = counts[tab];
  const visible = allData.slice(0, currentCount);
  const hasMore = currentCount < allData.length;

  const loadMore = () => setCounts((c) => ({ ...c, [tab]: c[tab] + 4 }));

  const [featured, ...rest] = visible;

  if (!projects?.length) return null;

  return (
    <Element name="projects">
      <section
        className="relative overflow-hidden"
        style={{ background: "#05050b" }}
      >
        {/* Grid bg */}
        <svg
          className="absolute inset-0 w-full h-full pointer-events-none opacity-[0.03]"
          aria-hidden
        >
          <defs>
            <pattern
              id="port-grid"
              width="72"
              height="72"
              patternUnits="userSpaceOnUse"
            >
              <path
                d="M 72 0 L 0 0 0 72"
                fill="none"
                stroke="white"
                strokeWidth="0.4"
              />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#port-grid)" />
        </svg>

        {/* Orbs */}
        <div
          className="absolute top-0 left-1/4 w-[500px] h-[500px] pointer-events-none"
          aria-hidden
        >
          <div
            style={{
              background:
                "radial-gradient(circle, rgba(0,71,255,0.1) 0%, transparent 70%)",
              filter: "blur(90px)",
              width: "100%",
              height: "100%",
            }}
          />
        </div>
        <div
          className="absolute bottom-0 right-1/4 w-[450px] h-[450px] pointer-events-none"
          aria-hidden
        >
          <div
            style={{
              background:
                "radial-gradient(circle, rgba(0,240,255,0.08) 0%, transparent 70%)",
              filter: "blur(90px)",
              width: "100%",
              height: "100%",
            }}
          />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-5 md:px-10 py-10 ">
          {/* ── HEADING ── */}
          <div ref={headRef} className="mb-16 lg:mb-20">
            <motion.div
              custom={0}
              variants={fadeUp}
              initial="hidden"
              animate={headInView ? "show" : "hidden"}
              className="flex items-center gap-4 mb-5"
            >
              <div className="h-px w-12 bg-gradient-to-r from-[#0047ff] to-[#00f0ff]" />
              <span
                className="text-[10px] tracking-[0.32em] text-[#00f0ff]/60 uppercase"
                style={{ fontFamily: "'DM Mono', monospace" }}
              >
                05 — Portfolio
              </span>
            </motion.div>

            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
              <div>
                <div className="overflow-hidden">
                  <motion.h2
                    custom={0.1}
                    variants={fadeUp}
                    initial="hidden"
                    animate={headInView ? "show" : "hidden"}
                    className="text-[clamp(3rem,9vw,7.5rem)] font-black leading-[0.88] tracking-tight text-white"
                    style={{ fontFamily: "'Syne', sans-serif" }}
                  >
                    SELECTED
                  </motion.h2>
                </div>
                <div className="overflow-hidden">
                  <motion.h2
                    custom={0.2}
                    variants={fadeUp}
                    initial="hidden"
                    animate={headInView ? "show" : "hidden"}
                    className="text-[clamp(3rem,9vw,7.5rem)] font-black leading-[0.88] tracking-tight"
                    style={{
                      fontFamily: "'Syne', sans-serif",
                      WebkitTextStroke: "1.5px rgba(0,240,255,0.32)",
                      color: "transparent",
                    }}
                  >
                    WORKS.
                  </motion.h2>
                </div>
                <motion.div
                  initial={{ scaleX: 0 }}
                  animate={headInView ? { scaleX: 1 } : {}}
                  transition={{
                    duration: 1,
                    delay: 0.35,
                    ease: [0.22, 1, 0.36, 1],
                  }}
                  className="mt-5 h-px w-40 origin-left"
                  style={{
                    background:
                      "linear-gradient(90deg, #0047ff, #00f0ff60, transparent)",
                  }}
                />
              </div>

              {/* Tab filters — right-aligned on desktop */}
              <motion.div
                custom={0.3}
                variants={fadeUp}
                initial="hidden"
                animate={headInView ? "show" : "hidden"}
                className="flex flex-wrap gap-2"
              >
                {tabs.map((t) => (
                  <TabButton
                    key={t.id}
                    tab={t}
                    active={tab === t.id}
                    onClick={setTab}
                  />
                ))}
              </motion.div>
            </div>
          </div>

          {/* ── PROJECT GRID ── */}
          <div ref={gridRef}>
            <AnimatePresence mode="wait">
              <motion.div
                key={tab}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              >
                {visible.length > 0 ? (
                  <motion.div
                    variants={gridStagger}
                    initial="hidden"
                    animate={gridInView ? "show" : "hidden"}
                    className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6"
                  >
                    {/* Featured — spans 2 cols */}
                    {featured && <FeaturedCard p={featured} />}

                    {/* Rest — standard cards */}
                    {rest.map((p, i) => (
                      <motion.div key={p?._id ?? i} variants={cardEntry}>
                        <PortfolioCard p={p} index={i + 1} />
                      </motion.div>
                    ))}
                  </motion.div>
                ) : (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="flex flex-col items-center justify-center py-28 gap-4"
                  >
                    <div
                      className="w-16 h-16 rounded-2xl border border-white/[0.06] flex items-center justify-center"
                      style={{ background: "rgba(0,71,255,0.05)" }}
                    >
                      <span className="text-[#00f0ff]/30 text-2xl">◫</span>
                    </div>
                    <p
                      className="text-sm text-white/25 tracking-[0.15em] uppercase"
                      style={{ fontFamily: "'DM Mono', monospace" }}
                    >
                      No projects in this category
                    </p>
                  </motion.div>
                )}
              </motion.div>
            </AnimatePresence>
          </div>

          {/* ── LOAD MORE ── */}
          {hasMore && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="flex justify-center mt-14"
            >
              <motion.button
                onClick={loadMore}
                whileHover={{ scale: 1.04 }}
                whileTap={{ scale: 0.96 }}
                className="relative group inline-flex items-center gap-3
                  px-8 py-3.5 rounded-full font-semibold text-sm tracking-[0.18em] uppercase
                  text-white/60 border border-white/[0.09]
                  hover:border-[#00f0ff]/35 hover:text-[#00f0ff] hover:bg-[#00f0ff]/[0.05]
                  transition-all duration-300 backdrop-blur-sm overflow-hidden"
                style={{ fontFamily: "'DM Mono', monospace" }}
              >
                <span className="relative z-10">Load More</span>
                <motion.span
                  className="relative z-10"
                  animate={{ y: [0, 4, 0] }}
                  transition={{ duration: 1.4, repeat: Infinity }}
                >
                  ↓
                </motion.span>
                {/* Shimmer */}
                <span
                  className="absolute inset-0 -translate-x-full group-hover:translate-x-full
                    transition-transform duration-700"
                  style={{
                    background:
                      "linear-gradient(90deg, transparent, rgba(0,240,255,0.07), transparent)",
                  }}
                />
              </motion.button>
            </motion.div>
          )}

          {/* ── FOOTER META ── */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8 }}
            className="mt-16 pt-7 flex items-center justify-between"
          >
            <div
              className="h-px flex-1 max-w-7xl"
              style={{
                background: "linear-gradient(90deg, #0047ff30, transparent)",
              }}
            />
            <div className="flex items-center gap-6 px-6">
              <span
                className="text-[9px] tracking-[0.25em] text-white/18 uppercase"
                style={{ fontFamily: "'DM Mono', monospace" }}
              >
                {visible.length} / {allData.length} shown
              </span>
              <span className="w-1 h-1 rounded-full bg-white/15" />
              <span
                className="text-[9px] tracking-[0.25em] text-white/18 uppercase"
                style={{ fontFamily: "'DM Mono', monospace" }}
              >
                {new Date().getFullYear()}
              </span>
            </div>
            <div
              className="h-px flex-1 max-w-7xl"
              style={{
                background: "linear-gradient(270deg, #00f0ff20, transparent)",
              }}
            />
          </motion.div>
        </div>
      </section>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@700;800;900&family=DM+Mono:ital,wght@0,300;0,400;0,500&family=DM+Sans:wght@300;400;500&display=swap');
      `}</style>
    </Element>
  );
};

export default Portfolio;
